import React, {useEffect, useState, useRef} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import PropTypes from 'prop-types';

import {getPercent} from '@ey/utils';
import {lightColor} from '@ey/styled-theme';

import {loadStorage, saveStorage} from '../../../../Common/localStorage';
import {labels} from '../../../../../util/utils';
import Utility from '../../../../../util/utilityfunctions';
import {dashboardChartColors, dashboardTabId, milestonesFilterId, projectSessionKeys} from '../../../../../util/uiconstants';
import normalizeDashboardFilters from '../../../../../util/dashboard/normalizeDashboardFilters';

import Tile from '../../../../Common/Tile';
import PieChart from '../../../../Common/Charts/PieChart';

import {
	updateFilterDashboardTiles,
	clearFilterDashboardTiles
} from '../../../../../actions/Metrics/dashboardFiltersAction';
import SelectWithCheckbox from '../../../../CoreWrappers/SelectWithCheckbox';

const MilestonesDetail = ({milestonesData}) => {
	const dispatch = useDispatch();

	const [isFirstLoad, setIsFirstLoad] = useState(true);
	const [selectedMilestones, setSelectedMilestones] = useState(null);
	const [chartData, setChartData] = useState([]);

	const selectedMilestonesRef = useRef();
	selectedMilestonesRef.current = selectedMilestones;

	const milestonesOption = Utility.getMilestoneOptionWithGroup();
	const projectId = Utility.getProjectIdFromUrl();
	const psessionKEy = `${projectSessionKeys.projectID}_${projectId}_${projectSessionKeys.milestones}`;

	useEffect(() => {
		const currentState = loadStorage(psessionKEy);
		if (currentState && currentState.SelectedMilestonesid) {
			const result = currentState.SelectedMilestonesid.map(({id}) => {
				let newValue = null;

				milestonesOption.forEach(({options}) =>
					options.forEach((option) => {
						if (option.id === id) newValue = {...option, checked: true};
					})
				);

				return newValue;
			});

			setSelectedMilestones(result.filter((item) => item !== null));
		} else {
			selectAllMillestones();
		}
	}, []);

	useEffect(() => {
		buildChartData(selectedMilestones);
	}, [milestonesData, selectedMilestones]);

	const buildChartData = (milestones) => {
		let filteredMilestones = [],
			milestonesList = milestones || [];

		// filtering the context for milestones
		if (milestones !== null) {
			filteredMilestones = milestonesData.filter((milestone) =>
				milestonesList.some((item) => item.id === milestone.tPTId)
			);
		} else {
			filteredMilestones = milestonesData;
		}

		// changing first load value to false after first valid load
		if (isFirstLoad && !_.isEmpty(milestonesList)) {
			setIsFirstLoad(false);
		}

		// counting partial totals
		let totalTaskCount = _.sum(filteredMilestones.map((item) => item.tT)),
			totalOpenTasks = _.sum(filteredMilestones.map((item) => item.tO)),
			totalInProgressTasks = _.sum(filteredMilestones.map((item) => item.tIP)),
			totalInReviewTasks = _.sum(filteredMilestones.map((item) => item.tIR)),
			totalCompletedTasks = _.sum(filteredMilestones.map((item) => item.tC)),
			totalOpenOverdueTasks = _.sum(filteredMilestones.map((item) => item.tNOD)),
			totalInReviewOverdueTasks = _.sum(filteredMilestones.map((item) => item.tIROD)),
			totalInProgressOverdueTasks = _.sum(filteredMilestones.map((item) => item.tIPOD));

		// building the series object for the pie chart
		let displayData = {
			total: totalTaskCount,
			series: [
				{
					name: labels.open,
					y: getPercent(totalOpenTasks, totalTaskCount),
					totalCount: totalOpenTasks,
					totalOverdue: totalOpenOverdueTasks,
					color: dashboardChartColors.green[0]
				},
				{
					name: labels.inProgress,
					y: getPercent(totalInProgressTasks, totalTaskCount),
					totalCount: totalInProgressTasks,
					totalOverdue: totalInProgressOverdueTasks,
					color: dashboardChartColors.green[1]
				},
				{
					name: labels.inReview,
					y: getPercent(totalInReviewTasks, totalTaskCount),
					totalCount: totalInReviewTasks,
					totalOverdue: totalInReviewOverdueTasks,
					color: dashboardChartColors.green[2]
				},
				{
					name: labels.completeUpperCase,
					y: getPercent(totalCompletedTasks, totalTaskCount),
					totalCount: totalCompletedTasks,
					color: dashboardChartColors.green[3]
				}
			]
		};

		setChartData(displayData);
	};

	const handlerOnClearFilter = () => {
		dispatch(clearFilterDashboardTiles(dashboardTabId.milestones));
	};

	const handlerOnGraphicClick = (highChartEvent) => {
		const selectedMilestones = selectedMilestonesRef.current;
		if (selectedMilestones?.length === 1) {
			const serie = highChartEvent.point.options.name;
			const category = selectedMilestones[0].label;

			const getFilteredEngagemets = () => {
				const filteredMilestones = milestonesData.filter((milestone) =>
					selectedMilestones.some((item) => item.id === milestone.tPTId)
				);

				switch (serie) {
					case labels.open:
						return filteredMilestones.filter((item) => item.tO > 0).map(({engId}) => engId);
					case labels.inProgress:
						return filteredMilestones.filter((item) => item.tIP > 0).map(({engId}) => engId);
					case labels.inReview:
						return filteredMilestones.filter((item) => item.tIR > 0).map(({engId}) => engId);
					case labels.completeUpperCase:
						return filteredMilestones.filter((item) => item.tC > 0).map(({engId}) => engId);
				}
			};

			//TODO: improve onClearChip.
			const dashboardFilters = normalizeDashboardFilters(
				[
					{
						serieLabel: serie,
						categoryLabel: category,
						type: milestonesFilterId.milestones,
						onClearChip: handlerOnClearFilter
					}
				],
				getFilteredEngagemets(),
				handlerOnClearFilter
			);

			dispatch(updateFilterDashboardTiles(dashboardTabId.milestones, dashboardFilters));
		} else {
			highChartEvent.preventDefault();
		}
	};

	const selectAllMillestones = () => {
		const allSelectedMilestones = milestonesOption
			.map((elem) => {
				return elem.options.map((e) => {
					return {...e, checked: true};
				});
			})
			.flat();

		setSelectedMilestones(allSelectedMilestones);
	};

	const renderHeader = () => {
		const handlerOnMultiSelectItemChange = (option) => {
			setSelectedMilestones(option);

			buildChartData(option);
			let currentState = loadStorage(psessionKEy);
			if (!currentState) {
				currentState = {};
			}
			currentState.SelectedMilestonesid = option;
			saveStorage(psessionKEy, currentState);
			if (option?.length !== 1) {
				handlerOnClearFilter();
			}
		};

		const props = {
			id: 'milestone-selectWhitCheckbox',
			value: selectedMilestones,
			options: milestonesOption,
			onChange: handlerOnMultiSelectItemChange,
			label: 'Milestones',
			labels: {
				allSelected: labels.allMilestones,
				multiSelect: labels.someMilestonesSelected,
				nonSelected: labels.noneSelected
			}
		};

		return <SelectWithCheckbox {...props} />;
	};

	const pieProps = {
		legendWidth: 180,
		labelCenterOffset: 10,
		data: chartData.series,
		total: chartData.total,
		totalText: labels.totalTasks,
		onGraphicClick: handlerOnGraphicClick
	};

	const tileProps = {
		title: labels.taskStatusByMilestone,
		subTitle: labels.basedOnAllTasksForTheSelectedMilestone,
		subHeader: renderHeader(),
		isEmpty: chartData.total === 0,
		emptyTitle: labels.noDataForSelections,
		emptySubtitle: labels.selectOneOrMoreMilestones
	};

	return (
		<Tile {...tileProps}>
			<PieChart {...pieProps} />
		</Tile>
	);
};

MilestonesDetail.defaultProps = {
	milestonesData: []
};

MilestonesDetail.propTypes = {
	milestonesData: PropTypes.arrayOf(
		PropTypes.shape({
			aPId: PropTypes.number,
			dO: PropTypes.number,
			eD: PropTypes.string,
			engKey: PropTypes.number,
			pC: PropTypes.number,
			pCG: PropTypes.string,
			pNO: PropTypes.number,
			pNOP: PropTypes.string,
			sD: PropTypes.string,
			tC: PropTypes.number,
			tIP: PropTypes.number,
			tIPOD: PropTypes.number,
			tIR: PropTypes.number,
			tIROD: PropTypes.number,
			tNOD: PropTypes.number,
			tO: PropTypes.number,
			tPTId: PropTypes.number,
			tT: PropTypes.number
		})
	)
};

export default MilestonesDetail;
